import { selectedAddresses, normalizeAddress } from './session';
import connector from './connector';

export default async function provisionReaders({ draft, snapshot, setup, url, active, show, waitFor, dispatch, services }) {
    const addresses = selectedAddresses(draft);
    const readers = snapshot.readers.filter(
        row =>
            (addresses.length
                ? addresses.includes(normalizeAddress(row.report?.address))
                : row.id === draft.readerId) &&
            row.report?.status === 'review' &&
            row.action !== 'apply' &&
            row.setupStatus !== 'Ready' &&
            !draft.reviewErrors?.[normalizeAddress(row.report?.address)]
    );
    if (!readers.length) throw new Error('Choose Check readers first.');
    const confirmed = await new Promise(resolve =>
        dispatch(
            {
                title: `Provision ${readers.length} reader${readers.length === 1 ? '' : 's'}`,
                message: [
                    `Connect ${readers.map(row => row.name.toLowerCase()).join(', ')} to ${snapshot.connection?.tenantTitle || 'this tenant'}?`,
                    ...readers.filter(row => row.report?.selectedRegion).map(row =>
                        `${row.name.toLowerCase()}: set country/region to ${row.report.selectedRegion.regionName}.`),
                    'Existing connections to another tenant will stop.',
                    addresses.length > readers.length ? 'Only ready readers will be provisioned.' : ''
                ].filter(Boolean).join('\n\n'),
                okButtonText: 'Confirm provisioning',
                cancelButtonText: 'Cancel',
                okAction: () => resolve(true),
                cancelAction: () => resolve(false)
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        )
    );
    if (!confirmed || !active()) return;
    draft.provisioningAddresses = readers.map(row => normalizeAddress(row.report?.address));
    await show({ ...draft, readyToConnect: false, requestStatus: connector.provisioningMessage });
    setup.connectingIds = [];
    for (const reader of readers) {
        if (!active()) return;
        try {
            const approval = {
                readerId: reader.id,
                operationId: reader.operationId,
                confirmed: true
            };
            const result = await services.http.post(`${url}/approve`, approval);
            if (result?.recoveryRequired) {
                const stopped = await new Promise(resolve => dispatch({
                    title: `Recover provisioning: ${reader.name.toLowerCase()}`,
                    message: 'The previous provisioning session ended before completion. Close the Reader Connector used for that session before continuing. This prevents two Connectors from changing the reader at the same time.',
                    okButtonText: 'Connector is closed',
                    cancelButtonText: 'Cancel',
                    okAction: () => resolve(true),
                    cancelAction: () => resolve(false)
                }, { verb: 'confirm', namespace: 'application', relation: 'user' }));
                if (!active()) return;
                if (!stopped) throw new Error('Close the previous Reader Connector before retrying provisioning.');
                await services.http.post(`${url}/approve`, { ...approval, recoverOperationId: result.operationId });
            }
            setup.connectingIds.push(reader.id);
        } catch (error) {
            if ([401, 403, 404].includes(error.status)) throw error;
            // Keep successful approvals; retrying the whole batch could reconnect them.
            draft.reviewErrors = {
                ...draft.reviewErrors,
                [normalizeAddress(reader.report.address)]: error.message
            };
        }
    }
    if (!active()) return;
    await show({
        ...draft,
        readyToConnect: false,
        requestStatus: connector.provisioningMessage
    });
    await waitFor(
        url,
        next => {
            return setup.connectingIds.every(id => {
                const reader = next.readers.find(row => row.id === id);
                return (
                    reader?.setupStatus === 'Ready' ||
                    reader?.setupStatus?.startsWith('Needs attention:') ||
                    reader?.report?.status === 'needs-attention'
                );
            });
        },
        150,
        false
    );
    if (!active()) return;
    draft = {
        ...setup.draft,
        requestStatus:
            setup.draft.readerProgress?.some(row => row.status === 'Provisioning')
                ? connector.provisioningMessage
                : setup.draft.batchProgress || setup.draft.readerError
    };
    return draft;
}
